import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'
import { Coupon } from '../../models/membership/Coupon'
import { couponSchema } from '../../models/schema'
import {
  createCoupon as createCouponAPI,
  deleteCoupon as deleteCouponAPI,
  fetchCouponById,
  fetchCoupons,
  updateCoupon as updateCouponAPI,
} from '../../services/CouponService'
import { AppDispatch, RootState } from '../store'

const initialState: SBState<Coupon> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'coupon',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getCouponsSuccess,
  setQuery: setCouponQuery,
  reset: resetCouponState,
  resetQueryAndIds: resetCouponQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<RootState, { [key: number]: Coupon }> = (
  state: RootState
) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectCoupons = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedCoupon = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectCouponById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectCouponsByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedCoupon =
  (coupon: Coupon | number) => async (dispatch: AppDispatch) =>
    dispatch(slice.actions.setSelectedId(getIdOrModelId<Coupon>(coupon)))

export const getCoupons = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchCoupons(params),
    [couponSchema],
    slice.actions
  )

export const createCoupon = (coupon: Coupon) =>
  SBAPI.CreateDispatched(createCouponAPI(coupon), couponSchema, slice.actions)

export const getCouponWithId = (id: number) =>
  SBAPI.FetchDispatched<Coupon>(
    fetchCouponById(id),
    couponSchema,
    slice.actions
  )

export const updateCoupon = (coupon: Coupon) =>
  SBAPI.UpdateDispatched<Coupon>(updateCouponAPI(coupon), coupon, slice.actions)

export const deleteCoupon = (coupon: Coupon) =>
  SBAPI.DestroyDispatched<Coupon>(
    deleteCouponAPI(coupon),
    coupon,
    slice.actions
  )
